import { applyMiddleware, combineReducers, compose, createStore } from 'redux';

import { persistStore, persistReducer, createMigrate } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import thunk from 'redux-thunk';
import { connectRouter, routerMiddleware } from 'connected-react-router';
import { History } from 'history';
import { ApplicationState, reducers } from './';
import {
  initialAssetsDataState,
  initialAssetsFilters,
  initialTicketFilters,
  initialDataState,
  initialAssetStatusesFilters,
  initialAssetStatusesGridDataState,
  initialWidgetsState,
  initialTicketsGridDataState,
  initialReportsFilter,
  defaultLanguage,
  initialVersionsGridDataState,
  initialProductsHealthGridDataState,
  initialHealthFilters,
  initialDeviceTrendsFilter,
  initialDeviceTrendsByDateDataState,
  initialNodesConfigFilters,
  initialNodesJsonConfigFilters,
  initialNodesConfigGridDataState,
  initialPrevMaintenanceGridDataState,
  initialPrevMaintenanceFilter,
  initialPredMaintenanceGridDataState,
  initialPredMaintenanceFilter,
  initialFirmwareGridDataState,
  initialProductsHealthFilters,
  initialNodesJsonConfigGridDataState,
} from './UserSettings/UserSettingsState';
import { initialDataState as initialAccessControlGridDataState } from './AccessControl/AccessControlState';

export default function configureStore(
  history: History,
  initialState?: ApplicationState,
) {
  const middleware = [thunk, routerMiddleware(history)];

  //Define the new state(s) default value(s)
  //Increment the version in persistConfig
  const migrations = {
    0: (state: any) => {
      return {
        ...state,
        userSettingsReducer: {
          ...state.userSettingsReducer,
          alertsGridDataState: initialDataState,
          nodesGridDataState: initialAssetsDataState,
          nodesFilters: initialAssetsFilters,
        },
      };
    },
    1: (state: any) => {
      return {
        ...state,
        userSettingsReducer: {
          ...state.userSettingsReducer,
        },
      };
    },
    2: (state: any) => {
      return {
        ...state,
        userSettingsReducer: {
          ...state.userSettingsReducer,
          assetStatusGridDataState: initialAssetStatusesGridDataState,
          assetStatusesFilters: initialAssetStatusesFilters,
        },
      };
    },
    3: (state: any) => {
      return {
        ...state,
        userSettingsReducer: {
          ...state.userSettingsReducer,
          ticketsDataState: initialTicketsGridDataState,
        },
      };
    },
    4: (state: any) => {
      return {
        ...state,
        userSettingsReducer: {
          ...state.userSettingsReducer,
          reportFilter: initialReportsFilter,
        },
      };
    },
    5: (state: any) => {
      return {
        ...state,
        userSettingsReducer: {
          ...state.userSettingsReducer,
          selectedLanguage: defaultLanguage,
        },
      };
    },
    6: (state: any) => {
      return {
        ...state,
        userSettingsReducer: {
          ...state.userSettingsReducer,
          ticketFilter: initialTicketFilters,
        },
      };
    },
    7: (state: any) => {
      return {
        ...state,
        userSettingsReducer: {
          ...state.userSettingsReducer,
        },
      };
    },
    8: (state: any) => {
      return {
        ...state,
        userSettingsReducer: {
          ...state.userSettingsReducer,
          versionsGridDataState: initialVersionsGridDataState,
        },
      };
    },
    9: (state: any) => {
      return {
        ...state,
        userSettingsReducer: {
          ...state.userSettingsReducer,
        },
      };
    },
    10: (state: any) => {
      return {
        ...state,
        userSettingsReducer: {
          ...state.userSettingsReducer,
          healthFilter: initialHealthFilters,
        },
      };
    },
    11: (state: any) => {
      return {
        ...state,
        userSettingsReducer: {
          ...state.userSettingsReducer,
          widgetsState: initialWidgetsState,
        },
      };
    },
    12: (state: any) => {
      return {
        ...state,
        userSettingsReducer: {
          ...state.userSettingsReducer,
          deviceTrendsFilter: initialDeviceTrendsFilter,
        },
      };
    },
    13: (state: any) => {
      return {
        ...state,
        userSettingsReducer: {
          ...state.userSettingsReducer,
          nodesConfigGridDataState: initialNodesConfigGridDataState,
          nodesConfigFilters: initialNodesConfigFilters,
        },
      };
    },
    14: (state: any) => {
      return {
        ...state,
        userSettingsReducer: {
          ...state.userSettingsReducer,
          prevMaintenanceGridDataState: initialPrevMaintenanceGridDataState,
          prevMaintenanceFilter: initialPrevMaintenanceFilter,
          predMaintenanceGridDataState: initialPredMaintenanceGridDataState,
          predMaintenanceFilter: initialPredMaintenanceFilter,
        },
      };
    },
    15: (state: any) => {
      return {
        ...state,
        userSettingsReducer: {
          ...state.userSettingsReducer,
          deviceTrendsByDateGridDataState: initialDeviceTrendsByDateDataState,
        },
      };
    },
    16: (state: any) => {
      return {
        ...state,
        userSettingsReducer: {
          ...state.userSettingsReducer,
          firmwareGridDataState: initialFirmwareGridDataState,
        },
      };
    },
    17: (state: any) => {
      return {
        ...state,
        accessControlReducer: {
          ...state.accessControlReducer,
          accessControlGridState: initialAccessControlGridDataState,
        },
      };
    },
    18: (state: any) => {
      return {
        ...state,
        userSettingsReducer: {
          ...state.userSettingsReducer,
          nodesJsonConfigFilters: initialNodesJsonConfigFilters,
        },
      };
    },
    19: (state: any) => {
      return {
        ...state,
        userSettingsReducer: {
          ...state.userSettingsReducer,
          productsHealthGridDataState: initialProductsHealthGridDataState,
          productsHealthFilter: initialProductsHealthFilters,
        },
      };
    },
    20: (state: any) => {
      return {
        ...state,
        userSettingsReducer: {
          ...state.userSettingsReducer,
          nodesJsonConfigGridDataState: initialNodesJsonConfigGridDataState,
        },
      };
    },
  };

  const rootReducer = combineReducers({
    ...reducers,
    router: connectRouter(history),
  });

  const persistConfig = {
    key: 'root',
    version: 20,
    whitelist: [
      'userSessionReducer',
      'userSettingsReducer',
      'accessControlReducer',
    ],
    blacklist: [
      'alertsReducer',
      'assetsReducer',
      'referenceWeightsReducer',
      'assetStatusesReducer',
      'reportsReducer',
      'versionsReducer',
      'ticketsReducer',
      'configSettingsReducer',
      'nbcReducer',
      'maintenanceReducer',
      'notificationsReducer',
      'productsReducer',
    ],
    storage: storage,
    migrate: createMigrate(migrations, { debug: false }),
  };
  const persistedReducer = persistReducer(persistConfig, rootReducer);

  const enhancers = [];
  const windowIfDefined =
    typeof window === 'undefined' ? null : (window as any); // eslint-disable-line @typescript-eslint/no-explicit-any
  if (windowIfDefined && windowIfDefined.__REDUX_DEVTOOLS_EXTENSION__) {
    enhancers.push(windowIfDefined.__REDUX_DEVTOOLS_EXTENSION__());
  }

  const store = createStore(
    persistedReducer,
    initialState,
    compose(applyMiddleware(...middleware), ...enhancers),
  );
  const persistor = persistStore(store);
  return { store, persistor };
}
